import React, { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, Edit3, ExternalLink, Gem, HelpCircle, TrendingUp, Users } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import toast from 'react-hot-toast'
import HelpModal from '../../../components/HelpModal'
import AnimatedButton from '../../../components/AnimatedButton'
import SeedEditor, { resetSavedSeed } from '../../../components/SeedEditor'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import useAppEnvironment from '../../../hooks/useAppEnvironment'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import UpgradeDial from '../components/UpgradeDial'
import UpgradeSkinCard from '../components/UpgradeSkinCard'
import UpgradeSelectionSheet from '../components/UpgradeSelectionSheet'
import useUpgraderGame from '../hooks/useUpgraderGame'

const GuestUpgrader = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const openLogin = useAppStore((state) => state.openLogin)
  const [showHelp, setShowHelp] = useState(false)

  return (
    <div className="mx-auto max-w-lg px-4 pb-12 pt-5 text-white sm:pt-8 lg:max-w-5xl lg:pt-3">
      <header className="mb-5 flex items-center justify-center gap-2 lg:mb-8">
        <button type="button" onClick={() => navigate('/games')} aria-label={t('common.back')} className="p-2 text-white active:scale-[0.98] lg:hidden"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="flex items-center gap-2 text-2xl text-accent-primary lg:text-4xl"><TrendingUp className="h-6 w-6 lg:h-8 lg:w-8" />{t('upgrader.title')}</h1>
        <button type="button" onClick={() => setShowHelp(true)} aria-label={t('upgrader.helpTitle')} className="p-2 text-text-secondary active:scale-[0.98]"><HelpCircle className="h-5 w-5" /></button>
      </header>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:items-center lg:gap-12">
        <UpgradeDial chance={null} spin={null} loading={null} outcome={null} emptyInventory guest />
        <div className="mt-6 space-y-5 text-center lg:mt-0">
          <div className="rounded-2xl border border-white/10 bg-surface p-6">
            <h2 className="text-xl font-bold">{t('upgrader.helpTitle')}</h2>
            <ol className="mt-4 space-y-3 text-left text-sm text-text-secondary">
              {[1, 2, 3, 4].map((step) => <li key={step}>{step}. {t(`upgrader.helpStep${step}`)}</li>)}
            </ol>
          </div>
          <p className="text-text-secondary">{t('guest.privateDescription')}</p>
          <button type="button" onClick={openLogin} className="w-full rounded-xl bg-accent-primary px-5 py-3 font-bold text-dark-primary active:scale-[0.98]">{t('guest.login')}</button>
          <button type="button" onClick={() => navigate('/cases')} className="px-4 py-2 font-semibold text-accent-primary active:scale-[0.98]">{t('upgrader.openCases')}</button>
        </div>
      </div>
      <TeamCapturePreview mode="upgrader" className="mt-8 mb-6 lg:ml-auto lg:w-full lg:max-w-[28rem]" />
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={t('upgrader.helpTitle')} steps={[1, 2, 3, 4].map((step) => t(`upgrader.helpStep${step}`))} buttonText={t('upgrader.helpGotIt')} />
    </div>
  )
}

const MemberUpgrader = () => {
  const { t, i18n } = useTranslation()
  const isRtl = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const location = useLocation()
  const loadProfile = useAppStore((state) => state.loadProfile)
  const { isWebBrowser } = useAppEnvironment()
  const { playSound, stopSound } = useAudioManager({ select: 'select', spin: 'upgraderSpin', win: 'caseMultiWinGood', fail: 'upgraderFail' })
  const [sheet, setSheet] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  const [showSeed, setShowSeed] = useState(false)
  const game = useUpgraderGame()
  const locked = game.loading && game.loading !== 'chance'
  const canUpgrade = game.source && game.target && game.chance !== null && !game.loading && !game.error
  const noTargets = game.source && !game.loading && !game.target && !game.error
  const showFirstSuccessActions = game.outcome?.win && game.outcome.firstSuccess
  const transitioning = game.loading === 'reset'
  const winningTransition = transitioning && game.outcome?.win
  const losingTransition = transitioning && game.outcome && !game.outcome.win
  const cardTransition = { duration: 0.65, ease: [0.22, 1, 0.36, 1] }

  useEffect(() => {
    if (!game.spin) return undefined
    playSound('spin')
    return () => stopSound('spin')
  }, [game.spin, playSound, stopSound])

  useEffect(() => {
    if (!game.outcome) return
    playSound(game.outcome.win ? 'win' : 'fail')
    hapticNotify(game.outcome.win ? 'success' : 'error')
    loadProfile().catch(() => {})
  }, [game.outcome, playSound, loadProfile])

  const openSheet = (mode) => {
    playSound('select')
    hapticImpact('light')
    setSheet(mode)
  }

  const handleCases = () => {
    playSound('select')
    hapticImpact('light')
    navigate('/cases')
  }

  const handleUpgrade = () => {
    if (!canUpgrade) return
    resetSavedSeed()
    if (window.ym) window.ym(104258304, 'reachGoal', 'start-upgrader')
    game.start()
  }

  const handleInvite = () => {
    playSound('select')
    hapticImpact('light')
    if (window.ym) window.ym(104258304, 'reachGoal', 'onboarding-invite-friend')
    if (isWebBrowser) {
      navigate('/referrals')
      return
    }
    const { shareInviteMessage, user } = useAppStore.getState()
    if (!user?.ref_link) {
      toast.error(t('referrals.linkNotFound'))
      return
    }
    shareInviteMessage(t('invite.message'), user.ref_link, {
      shareTitle: t('invite.shareTitle'),
      shareMessage: t('invite.shareMessage'),
      copy: t('invite.copy'),
      close: t('common.close'),
      copySuccess: t('invite.copySuccess'),
      copySuccessBrowser: t('invite.copySuccessBrowser'),
      copyManual: t('invite.copyManual'),
      createMessageError: t('invite.createMessageError')
    })
  }

  return (
    <div className="mx-auto max-w-lg px-4 pb-12 pt-5 text-white sm:pt-8 lg:max-w-5xl lg:pt-3">
      <header className="mb-5 text-center lg:mb-8">
        <div className="flex items-center justify-center gap-2">
          <button type="button" onClick={() => navigate('/games')} aria-label={t('common.back')} className="p-2 text-white active:scale-[0.98] lg:hidden"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="flex items-center justify-center gap-2 text-2xl text-accent-primary lg:text-4xl"><TrendingUp className="h-6 w-6 shrink-0 lg:h-8 lg:w-8" />{t('upgrader.title')}</h1>
          <button type="button" onClick={() => { playSound('select'); hapticImpact('light'); setShowHelp(true) }} aria-label={t('upgrader.helpTitle')} className="p-2 text-text-secondary active:scale-[0.98]"><HelpCircle className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:items-center lg:gap-12">
        <UpgradeDial chance={game.chance} spin={game.spin} loading={game.loading} outcome={game.outcome} emptyInventory={!game.source && game.inventory.length === 0} />

        <div className="min-w-0">
          <AnimatePresence initial={false}>
            {game.source && (
              <motion.div key="skin-pair" exit={{ opacity: 0, y: game.outcome && !game.outcome.win ? 100 : -12 }} transition={cardTransition} className="relative mt-5 grid grid-cols-2 gap-3 lg:mt-0">
                <AnimatePresence initial={false} mode="popLayout">
                  <motion.div
                    key={`source-${game.source.id}`}
                    initial={winningTransition ? { opacity: 0, scale: 1.2 } : losingTransition ? { opacity: 0, y: -25 } : false}
                    animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
                    exit={game.loading === 'result' ? game.outcome?.win ? { opacity: 0, scale: 0.7 } : { opacity: 0, y: 100, rotate: -8 } : undefined}
                    transition={transitioning ? { ...cardTransition, delay: winningTransition ? 0.4 : 0.2 } : cardTransition}
                    className="min-w-0"
                  >
                    <UpgradeSkinCard skin={game.source} label={t('upgrader.sourceItem')} onClick={() => openSheet('source')} disabled={locked} />
                  </motion.div>
                  {game.target ? (
                    <motion.div
                      key={`target-${game.source.id}-${game.target.id}`}
                      initial={winningTransition ? { opacity: 0, scale: 0.7 } : losingTransition ? { opacity: 0, y: -25 } : false}
                      animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
                      exit={game.loading === 'result' ? game.outcome?.win ? { x: isRtl ? 'calc(100% + 12px)' : 'calc(-100% - 12px)', scale: [1, 1.14, 1], opacity: [1, 1, 0], transition: { ...cardTransition, opacity: { duration: 1, times: [0, 0.65, 1], ease: 'easeOut' } }, transitionEnd: { visibility: 'hidden' } } : { opacity: 0, y: 100, rotate: 8 } : undefined}
                      transition={transitioning ? { ...cardTransition, delay: 0.2 } : cardTransition}
                      className="relative z-[1] min-w-0"
                    >
                      <UpgradeSkinCard skin={game.target} label={t('upgrader.targetItem')} multiplier={game.multiplier} isTarget result={game.loading === 'result' ? (game.outcome?.win ? 'win' : 'lose') : null} onClick={() => openSheet('target')} disabled={locked} />
                    </motion.div>
                  ) : (
                    <motion.div key="target-empty" initial={transitioning ? { opacity: 0, scale: 0.7 } : false} animate={{ opacity: 1, x: 0, scale: 1 }} transition={cardTransition} className="min-w-0">
                      <button type="button" onClick={() => openSheet('target')} disabled={locked || !Object.keys(game.groups).length} className="flex h-full w-full min-h-48 items-center justify-center rounded-2xl border border-white/10 bg-surface p-3 text-center text-sm text-text-secondary active:scale-[0.98] disabled:active:scale-100">{game.loading ? t('common.loading') : t('upgrader.step2SelectTarget')}</button>
                    </motion.div>
                  )}
                </AnimatePresence>
                <span className="pointer-events-none absolute left-1/2 top-[42%] z-10 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border border-white/20 bg-dark-primary text-accent-primary shadow-lg">{isRtl ? <ArrowLeft className="h-6 w-6" /> : <ArrowRight className="h-6 w-6" />}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {game.error && game.error !== 'prize' && (
            <div role="alert" className="mt-4 rounded-xl border border-error/30 bg-error/10 px-4 py-3 text-sm text-white">
              <p>{t(game.error === 'request' ? 'upgraderNew.requestUnknown' : 'upgraderNew.loadFailed')}</p>
              <button type="button" onClick={() => { playSound('select'); hapticImpact('light'); game.retry() }} className="mt-2 font-semibold text-accent-primary underline underline-offset-2">{t('common.retry')}</button>
            </div>
          )}

          {!showFirstSuccessActions && !game.loading && !game.source && (!game.error || (game.error === 'prize' && game.inventory.length > 0)) && (
            <div className={`mt-4 px-5 py-8 text-center lg:mt-0 ${game.inventory.length ? 'rounded-2xl border border-white/10 bg-surface' : ''}`}>
              <Gem className="mx-auto mb-3 h-10 w-10 text-text-secondary" />
              <p className="font-semibold">{game.inventory.length ? t('upgraderNew.selectSkin') : t('upgrader.noItems')}</p>
              <button type="button" onClick={() => game.inventory.length ? openSheet('source') : handleCases()} className="mt-4 rounded-xl border border-accent-primary/40 bg-accent-primary/10 px-5 py-3 font-semibold text-accent-primary active:scale-[0.98]">{t(game.inventory.length ? 'upgrader.selectAnotherItem' : 'upgrader.openCases')}</button>
            </div>
          )}

          {noTargets && <p className="mt-4 text-center text-sm text-text-secondary">{t('upgrader.noMultipliers')}</p>}

          {game.source && !showFirstSuccessActions && (
            <div className="mt-4">
              <AnimatedButton onClick={handleUpgrade} disabled={!canUpgrade} className="!text-lg"><TrendingUp className="h-5 w-5" />{['request', 'spin', 'result', 'reset'].includes(game.loading) ? t('upgraderNew.upgrading') : game.loading ? t('common.loading') : t('upgrader.upgrade')}</AnimatedButton>
              <button type="button" onClick={() => { playSound('select'); hapticImpact('light'); setShowSeed(true) }} disabled={locked} className="mx-auto mt-3 flex items-center gap-2 text-sm text-text-secondary active:scale-[0.98] disabled:opacity-50"><Edit3 className="h-4 w-4" />{t('seedEditor.editSeed')}</button>
            </div>
          )}

          {showFirstSuccessActions && <div className="mt-5 space-y-3">
            <button type="button" onClick={handleInvite} className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-3 font-bold text-dark-primary shadow-lg shadow-accent-primary/20 active:scale-[0.98]">
              <Users size={22} className="shrink-0" />
              <span className="flex flex-col items-start text-left"><span>{t('upgrader.inviteFriendButton')}</span><span className="flex items-center gap-1 text-sm">{t('upgrader.inviteFriendReward')}<Gem size={15} /></span></span>
            </button>
            <button type="button" onClick={() => { playSound('select'); hapticImpact('light'); navigate('/inventory') }} className="mx-auto flex items-center gap-1.5 px-3 py-2 text-sm text-text-secondary active:scale-[0.98]">
              <ExternalLink size={16} />{t('upgrader.withdrawToSteam')}
            </button>
          </div>}
        </div>
      </div>
      {location.state?.fromOnboarding !== true && <TeamCapturePreview mode="upgrader" className="mt-8 mb-6 lg:ml-auto lg:w-full lg:max-w-[28rem]" />}

      {sheet && <UpgradeSelectionSheet mode={sheet} onClose={() => setSheet(null)} inventory={game.inventory} source={game.source} groups={game.groups} multiplier={game.multiplier} target={game.target} onSource={game.selectSource} onMultiplier={game.selectMultiplier} onTarget={game.selectTarget} />}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={t('upgrader.helpTitle')} steps={[t('upgrader.helpStep1'), t('upgrader.helpStep2'), t('upgrader.helpStep3'), t('upgrader.helpStep4')]} buttonText={t('upgrader.helpGotIt')} />
      <SeedEditor isOpen={showSeed} onClose={() => setShowSeed(false)} />
    </div>
  )
}

const UpgraderNew = () => {
  const isGuest = useAppStore((state) => state.isGuest)
  return isGuest ? <GuestUpgrader /> : <MemberUpgrader />
}

export default UpgraderNew
